import { useLocale, useTranslations } from 'next-intl';
import { useState, useRef, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';

import { LocationIcon } from '@/component/Icons/LocationIcon';
import { useListingSearchStore } from '@/store';
import { showShortDate } from '@/utils/common';
import { useSearchSubmit } from '../../hooks';
import { LocationModal, useSearchLocations } from '../LocationField';
import { DateModal } from '../DateField';
import { GuestsModal } from '../GuestsField';

import styles from './SearchInputMobileField.module.css';

export const SearchInputMobileField = ({ currentStep, onSetStep }) => {
  const {
    setLocation,
    location,
    invalidField,
    setInvalidField,
    setDate,
    date,
    setInexactStay,
    inexactStay,
    guests,
    onReset,
  } = useListingSearchStore();
  const isMobile = useMediaQuery({ query: '(max-width: 769px)' });
  const ref = useRef();
  const locale = useLocale();
  const t = useTranslations();

  const onSearchClick = () => {
    if (isMobile && !currentStep) {
      onSetStep('location');
    }
  };

  const handleClearValue = (e) => {
    if (e) e.stopPropagation();
    setLocation();
    onSetStep(null);
  };

  const handleCloseModal = (e) => {
    if (e) e.stopPropagation();
    onSetStep(null);
  };

  const handleNextStep = (e) => {
    e.stopPropagation();
    if (currentStep === 'location') {
      onSetStep('date');
    } else if (currentStep === 'date') {
      onSetStep('guests');
    }
  };

  const handleSelect = (item) => {
    setLocation(item.data);
    onSetStep('date');
  };

  useEffect(() => {
    if (invalidField === 'location') {
      setInvalidField(null);
      ref.current?.focus();
    }
  }, [invalidField]);

  const handleClearAll = () => {
    handleClearValue();
    onReset();
  };

  const { onSearch, redirecting } = useSearchSubmit();
  const onSearchListing = () => {
    onSearch();
    onSetStep(null);
  };

  return (
    <section
      onClick={onSearchClick}
      className={`search-summary-wrap mr-3 ${styles.location}`}
    >
      <div className={`search-summary-box ${styles.box}`}>
        <div className={styles.input}>
          <span className={`search-summary-icon ${styles.icon}`}>
            <LocationIcon />
          </span>

          <div className="flex flex-col gap-2 w-full">
            <label className="text-xs leading-[9px] text-slate-600">
              <span>{location?.name || t('where')}</span>
            </label>
            <input
              className="search-summary-input text-xs font-medium inline-block w-full mt-[-7px] mb-[-6px] placeholder:text-slate-400 bg-transparent focus:outline-none text-black"
              value={formatDateAndGuests({ date, guests, locale })}
              placeholder={t('search-location')}
              ref={ref}
              tabIndex={1}
              readOnly={true}
            />
          </div>
        </div>
      </div>

      {currentStep === 'location' && isMobile && (
        <LocationModalWrapper
          location={location}
          onClose={handleCloseModal}
          onSubmit={handleNextStep}
          onSelect={handleSelect}
        />
      )}
      {currentStep === 'date' && isMobile && (
        <DateModal
          onClose={handleCloseModal}
          onSubmit={handleNextStep}
          date={date}
          onDateChange={setDate}
          inexactStay={inexactStay}
          onSelectInexactDays={setInexactStay}
        />
      )}
      {currentStep === 'guests' && isMobile && (
        <GuestsModal
          onSearch={onSearchListing}
          onClearAll={handleClearAll}
          loading={redirecting}
        />
      )}
    </section>
  );
};

function LocationModalWrapper({ location, onClose, onSubmit, onSelect }) {
  const [value, setValue] = useState(location?.name);
  const {
    data: items,
    loading,
    isEmpty: isItemsEmpty,
  } = useSearchLocations({ value });
  const onSearchChange = (e) => {
    setValue(e.target.value);
  };
  const handleSelect = (item) => {
    setValue(item.value);
    if (onSelect) onSelect(item);
  };
  useEffect(() => {
    // HACK: if location was cleared globally we have to reset local state
    if (value && !location?.name) {
      setValue('');
    }
  }, [location]);
  return (
    <LocationModal
      onClose={onClose}
      onSubmit={onSubmit}
      search={value}
      onSearchChange={onSearchChange}
      onSelect={handleSelect}
      items={items}
      loading={loading}
      isItemsEmpty={isItemsEmpty}
    />
  );
}

function pluralize(value, single, plural) {
  return `${value} ${value === 1 ? single : plural}`;
}

function formatDateAndGuests({ date, guests, locale }) {
  const start = date?.startDate ? showShortDate(date.startDate, locale) : '';
  const end = date?.endDate ? showShortDate(date.endDate, locale) : '';
  const dates = start && end ? `${start} - ${end}` : '';
  const adults = guests.adults
    ? pluralize(guests.adults, 'guest', 'guests')
    : '';
  const infants = guests.infants
    ? pluralize(guests.infants, 'infant', 'infants')
    : '';
  const children = guests.children
    ? pluralize(guests.children, 'child', 'children')
    : '';
  const people = adults
    ? [adults, children, infants].filter(Boolean).join(', ')
    : '';
  return [dates, people].filter(Boolean).join(' • ');
}
